import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  BookmarkIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  EllipsisVerticalIcon,
  LucideIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { ClearFiltersButton } from '@/components/ui/custom/ComplexFilter/ClearFiltersButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import { closeLogsFilterPanel, openLogsFilterPanel } from '@/modules/analytics/redux/actions';
import { selectLogsFiltersPanel } from '@/modules/analytics/redux/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { FilterViewListItem, InsightFilter, LogsAnalytics } from '@/shared/types/analytics';
import { Brain } from '@/shared/types/brain';
import { Desk } from '@/shared/types/desk';
import { Insight } from '@/shared/types/insights';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import {
  formatFilterValue,
  formatInsightFilter,
  getFilterIcon,
  getFilterTitle,
} from '@/util/filterFormatters';
import { noop } from '@/util/util';

import { FilterItem } from './FilterItem';
import { SaveFilterForm } from './SaveFilterForm';
import { SessionFilterDropdown } from './SessionFilterDropdown';
import { mapDataModelToUiType } from './sessionFilterUtils';

interface FiltersBarProps {
  filters?: LogsAnalytics[];
  onRemoveFilter?: (filterType: string) => void;
  desks?: Desk[];
  brains?: Brain[];
  languageModelNames?: Record<string, string>;
  scope?: 'brain' | 'account';
  // Action buttons props
  onSaveView?: (data: { name: string; overwriteExisting: boolean }) => Promise<void>;
  onUpdateActiveView?: () => void;
  // Filter view actions
  onDeleteView?: () => void;
  onSelectView?: (viewId: string) => void;
  // Filter view props
  activeView?: FilterViewListItem;
  existingViews?: FilterViewListItem[];
  activeViewId?: string;
  hasUnsavedChanges?: boolean;
  isCheckingMatch?: boolean;
  filterIndexByType?: Record<string, number>;
  removeFilterAtIndex?: (index: number) => void;
  onSaveFilters?: () => void;
  onClearAll?: () => void;
  insights?: Insight[];
}

/**
 * Component that displays customization options with save/cancel actions
 */
export const FiltersBar: React.FC<FiltersBarProps> = React.memo(
  ({
    filters = [],
    onRemoveFilter,
    desks,
    brains,
    languageModelNames,
    scope = 'brain',
    onSaveView,
    onUpdateActiveView,
    onDeleteView,
    onSelectView,
    activeView,
    existingViews = [],
    activeViewId,
    hasUnsavedChanges = false,
    isCheckingMatch = false,
    filterIndexByType,
    removeFilterAtIndex,
    onSaveFilters,
    onClearAll,
    insights,
  }) => {
    const { t } = useTranslation();
    const { members } = useMembers();
    const dispatch = useDispatch();
    const { openFilter, openFilterIndex, openSource } = useSelector(selectLogsFiltersPanel);
    const canWriteFilters = useSelector((state: RootState) =>
      getPermissions(state, 'filters', actions.WRITE)
    );
    const [saveDropdownOpen, setSaveDropdownOpen] = useState(false);
    const [editDropdownOpen, setEditDropdownOpen] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showSaveAsNewForm, setShowSaveAsNewForm] = useState(false);
    const [viewSelectorOpen, setViewSelectorOpen] = useState(false);

    // Track when popover was opened to prevent immediate close from focus events
    // This handles the case when popover is opened programmatically from the dropdown
    const popoverOpenTimeRef = useRef<number>(0);

    useEffect(() => {
      if (openFilter && openSource === 'pill') {
        popoverOpenTimeRef.current = Date.now();
      }
    }, [openFilter, openFilterIndex, openSource]);

    const indexByType = filterIndexByType ?? {};
    const insightLookup = useMemo(() => {
      if (!insights || insights.length === 0) {
        return new Map<string, Insight>();
      }
      const map = new Map<string, Insight>();
      insights.forEach((insight) => {
        map.set(insight.insight_id, insight);
        map.set(insight.name, insight);
      });
      return map;
    }, [insights]);

    // Show bar only if filters exist
    if (filters.length === 0) {
      return null;
    }

    // Determine when to show action buttons
    const shouldShowActionButtons =
      hasUnsavedChanges ||
      (!activeView && filters.length > 0) ||
      (activeView && (onSaveView || onDeleteView)) ||
      !!onClearAll;

    const handleClosePanel = () => {
      if (openSource === 'pill') {
        dispatch(closeLogsFilterPanel());
      }
    };

    const handlePopoverOpenChange =
      (filterKey: string, filterIndex: number | undefined) => (nextOpen: boolean) => {
        if (filterIndex === undefined) {
          return;
        }
        if (nextOpen) {
          dispatch(openLogsFilterPanel({ filterKey, filterIndex, source: 'pill' }));
        } else {
          dispatch(closeLogsFilterPanel());
        }
      };

    const handlePopoverClear = (filterIndex: number | undefined) => {
      if (filterIndex === undefined) {
        return;
      }
      removeFilterAtIndex?.(filterIndex);
      onSaveFilters?.();
      dispatch(closeLogsFilterPanel());
    };

    // Prevent popover from closing due to focus events when opened programmatically
    // Grace period of 300ms allows the dropdown to fully close before we allow outside interactions
    const shouldPreventPopoverClose = () => {
      const timeSinceOpen = Date.now() - popoverOpenTimeRef.current;
      return timeSinceOpen < 300;
    };

    const renderFilterPopover = ({
      key,
      icon,
      title,
      text,
      filterKey,
      filterIndex,
      onRemove,
    }: {
      key: string;
      icon?: LucideIcon;
      title: string;
      text: string;
      filterKey: string;
      filterIndex: number | undefined;
      onRemove: () => void;
    }) => {
      if (filterIndex === undefined) {
        return <FilterItem key={key} icon={icon} title={title} text={text} onClose={onRemove} />;
      }

      const isOpen =
        openSource === 'pill' && openFilter === filterKey && openFilterIndex === filterIndex;

      return (
        <Popover
          key={key}
          open={isOpen}
          onOpenChange={handlePopoverOpenChange(filterKey, filterIndex)}
        >
          <PopoverTrigger asChild>
            <FilterItem
              icon={icon}
              title={title}
              text={text}
              onClose={() => {
                handleClosePanel();
                onRemove();
              }}
              isActive={isOpen}
              className="cursor-pointer"
            />
          </PopoverTrigger>
          <PopoverContent
            className="w-auto border-none bg-transparent p-0 shadow-none"
            align="start"
            sideOffset={8}
            onInteractOutside={(e) => {
              if (shouldPreventPopoverClose()) {
                e.preventDefault();
              }
            }}
            onFocusOutside={(e) => {
              if (shouldPreventPopoverClose()) {
                e.preventDefault();
              }
            }}
          >
            <SessionFilterDropdown
              type={filterKey}
              key={filterKey}
              filterIndex={filterIndex}
              onClose={() => dispatch(closeLogsFilterPanel())}
              onApply={onSaveFilters ?? noop}
              onClear={() => handlePopoverClear(filterIndex)}
              variant="popover"
              scope={scope}
            />
          </PopoverContent>
        </Popover>
      );
    };

    // Show skeleton while checking for matching filter views
    if (isCheckingMatch && filters.length > 0) {
      return (
        <div className="border-color-stroke relative mb-4 flex min-h-9 w-full rounded-lg border bg-background p-1.5">
          <div className="flex flex-1 items-center gap-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-40" />
          </div>
        </div>
      );
    }

    return (
      <div className="border-color-stroke relative mb-4 flex min-h-9 w-full rounded-lg border bg-background p-1.5">
        {/* Filter items container - can wrap */}
        <div
          className={cn(
            'flex flex-1 flex-wrap items-center gap-2',
            shouldShowActionButtons && 'pr-20'
          )}
        >
          {/* Active filter view display with dropdown to switch views */}
          {activeView && (
            <>
              <DropdownMenu open={viewSelectorOpen} onOpenChange={setViewSelectorOpen}>
                <InfoTooltip content={activeView.name} side="top" size="large">
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost-neutral"
                      className="h-auto gap-1.5 px-2 py-1.5 font-semibold"
                    >
                      <BookmarkIcon />
                      <span className="max-w-32 truncate">{activeView.name}</span>
                      {existingViews.length > 1 && <ChevronsUpDownIcon />}
                    </Button>
                  </DropdownMenuTrigger>
                </InfoTooltip>
                {existingViews.length > 1 && onSelectView && (
                  <DropdownMenuContent align="start" className="min-w-40">
                    {existingViews.map((view) => (
                      <DropdownMenuItem
                        key={view.id}
                        className="relative pr-8"
                        onSelect={() => onSelectView(view.id)}
                      >
                        {view.name}
                        {view.id === activeViewId && (
                          <CheckIcon size={16} className="absolute right-2 text-muted-foreground" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
              <Separator orientation="vertical" className="h-8" />
            </>
          )}

          {filters.flatMap((filter) => {
            if (filter.type === 'insightFilters' && filter.insightFilters) {
              const insightFilterIndex = indexByType.insightFilters;

              return filter.insightFilters.map((insightFilter: InsightFilter) => {
                const matchingInsight = insightLookup.get(insightFilter.id);
                const filterKey = matchingInsight
                  ? `insight-${matchingInsight.insight_id}`
                  : `insight-${insightFilter.id}`;

                return renderFilterPopover({
                  key: `insight-${insightFilter.id}`,
                  icon: getFilterIcon('insightFilters'),
                  title: insightFilter.id || 'Insight',
                  text: formatInsightFilter(insightFilter, t),
                  filterKey,
                  filterIndex: insightFilterIndex,
                  onRemove: () => {
                    onRemoveFilter?.(`insightFilter-${insightFilter.id}`);
                  },
                });
              });
            }

            const filterValue = formatFilterValue(
              filter,
              t,
              desks,
              brains,
              languageModelNames,
              scope,
              members
            );
            if (!filterValue) {
              return [];
            }

            const filterType = filter.type;
            const uiFilterKey = mapDataModelToUiType(filterType);
            const filterIndex = indexByType[filterType];

            return [
              renderFilterPopover({
                key: filterType,
                icon: getFilterIcon(filterType),
                title: getFilterTitle(filterType, t, scope),
                text: filterValue,
                filterKey: uiFilterKey,
                filterIndex,
                onRemove: () => {
                  onRemoveFilter?.(filter.type);
                },
              }),
            ];
          })}
        </div>

        {/* Action buttons area - fixed position on the top right */}
        {shouldShowActionButtons && (
          <div className="absolute top-1.5 right-1.5 flex items-center gap-1.5">
            {onClearAll && (
              <ClearFiltersButton onClick={onClearAll} activeFilterCount={filters.length} />
            )}
            {/* Save button - only show when there are unsaved changes or new filters without a view */}
            {canWriteFilters &&
              (hasUnsavedChanges || (!activeView && filters.length > 0)) &&
              (activeView && onUpdateActiveView && onSaveView ? (
                <Popover
                  open={saveDropdownOpen}
                  onOpenChange={(open) => {
                    setSaveDropdownOpen(open);
                    if (!open) {
                      setShowSaveAsNewForm(false);
                    }
                  }}
                >
                  <InfoTooltip content={t('brain_logs.save_filter_view')} side="top">
                    <PopoverTrigger asChild>
                      <Button size="icon-sm" variant="primary">
                        <BookmarkIcon size={16} />
                      </Button>
                    </PopoverTrigger>
                  </InfoTooltip>
                  <PopoverContent align="end" className="w-auto p-0">
                    {!showSaveAsNewForm ? (
                      <div className="flex min-w-32 flex-col p-1">
                        <button
                          type="button"
                          className="relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm leading-5 outline-none select-none hover:bg-selected-soft"
                          onClick={() => {
                            onUpdateActiveView();
                            setSaveDropdownOpen(false);
                          }}
                        >
                          {t('common.save')}
                        </button>
                        <button
                          type="button"
                          className="relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm leading-5 outline-none select-none hover:bg-selected-soft"
                          onClick={() => setShowSaveAsNewForm(true)}
                        >
                          {t('brain_logs.save_as_new')}
                        </button>
                      </div>
                    ) : (
                      <SaveFilterForm
                        onSubmit={onSaveView}
                        existingViews={existingViews}
                        activeViewId={activeViewId}
                        onSuccess={() => {
                          setShowSaveAsNewForm(false);
                          setSaveDropdownOpen(false);
                        }}
                        onCancel={() => setShowSaveAsNewForm(false)}
                      />
                    )}
                  </PopoverContent>
                </Popover>
              ) : (
                onSaveView && (
                  <Popover open={saveDropdownOpen} onOpenChange={setSaveDropdownOpen}>
                    <InfoTooltip content={t('brain_logs.save_filter_view')} side="top">
                      <PopoverTrigger asChild>
                        <Button size="icon-sm" variant="primary">
                          <BookmarkIcon size={16} />
                        </Button>
                      </PopoverTrigger>
                    </InfoTooltip>
                    <PopoverContent align="end" className="w-auto p-0">
                      <SaveFilterForm
                        onSubmit={onSaveView}
                        existingViews={existingViews}
                        activeViewId={activeViewId}
                        onSuccess={() => setSaveDropdownOpen(false)}
                      />
                    </PopoverContent>
                  </Popover>
                )
              ))}

            {/* Filter view actions menu - always show when there's an active view */}
            {activeView && (onSaveView || onDeleteView) && (
              <Popover
                open={editDropdownOpen}
                onOpenChange={(open) => {
                  setEditDropdownOpen(open);
                  if (!open) {
                    setShowEditForm(false);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button variant="ghost-neutral" size="icon-sm">
                    <EllipsisVerticalIcon size={16} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-auto p-0">
                  {!showEditForm ? (
                    <div className="flex min-w-32 flex-col p-1">
                      {onSaveView && (
                        <button
                          type="button"
                          className="relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm leading-5 outline-none select-none hover:bg-selected-soft [&_svg]:size-4 [&_svg]:text-muted-foreground"
                          onClick={() => setShowEditForm(true)}
                        >
                          <PencilIcon />
                          {t('common.edit_name')}
                        </button>
                      )}
                      {onDeleteView && (
                        <button
                          type="button"
                          className="relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm leading-5 text-destructive outline-none select-none hover:bg-destructive/10 [&_svg]:size-4"
                          onClick={() => {
                            onDeleteView();
                            setEditDropdownOpen(false);
                          }}
                        >
                          <Trash2Icon />
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                  ) : (
                    <SaveFilterForm
                      onSubmit={async (data) => {
                        await onSaveView?.({ name: data.name, overwriteExisting: true });
                      }}
                      existingViews={existingViews}
                      activeViewId={activeViewId}
                      initialValue={activeView.name}
                      title={t('brain_logs.rename_filter_view')}
                      onSuccess={() => {
                        setShowEditForm(false);
                        setEditDropdownOpen(false);
                      }}
                      onCancel={() => setShowEditForm(false)}
                    />
                  )}
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>
    );
  }
);

FiltersBar.displayName = 'FiltersBar';
